import React, { useState } from "react";
import { memoryService, Memory, UpdateMemoryDto } from "../services/memory.service";
import { useToast } from "../context/ToastContext";
import "../styles/clients.css";

interface Props {
    entry: Memory;
    onClose: () => void;
    onUpdated: () => void;
}

export default function EditKnowledgeModal({ entry, onClose, onUpdated }: Props) {
    const { showToast } = useToast();
    const [question, setQuestion] = useState(entry.question);
    const [answer, setAnswer] = useState(entry.answer);
    const [category, setCategory] = useState(entry.metadata?.category || "");
    const [isPublic, setIsPublic] = useState(entry.metadata?.isPublic || false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const payload: UpdateMemoryDto = {};
            if (question.trim() !== entry.question) payload.question = question.trim();
            if (answer.trim() !== entry.answer) payload.answer = answer.trim();
            if (category !== (entry.metadata?.category || "")) payload.category = category || undefined;
            if (isPublic !== (entry.metadata?.isPublic || false)) payload.isPublic = isPublic;

            if (Object.keys(payload).length === 0) {
                onClose();
                return;
            }

            await memoryService.update(entry.id, payload);
            showToast("Knowledge entry updated successfully.");
            onUpdated();
        } catch (err: any) {
            setError(err.response?.data?.msg || "Failed to update knowledge entry.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <h2>Edit Knowledge</h2>
                <p className="modal-subtitle">Update this Q&A entry</p>

                <form className="modal-form" onSubmit={handleSubmit}>
                    {error && <div className="modal-error">{error}</div>}

                    <div className="modal-field">
                        <label htmlFor="edit-kb-question">Question</label>
                        <textarea
                            id="edit-kb-question"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            autoFocus
                        />
                    </div>

                    <div className="modal-field">
                        <label htmlFor="edit-kb-answer">Answer</label>
                        <textarea
                            id="edit-kb-answer"
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                        />
                    </div>

                    <div className="modal-field">
                        <label htmlFor="edit-kb-category">Category</label>
                        <input
                            id="edit-kb-category"
                            type="text"
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            placeholder="e.g. faq, product, policy"
                        />
                    </div>

                    <div className="modal-checkbox">
                        <input
                            id="edit-kb-public"
                            type="checkbox"
                            checked={isPublic}
                            onChange={(e) => setIsPublic(e.target.checked)}
                        />
                        <label htmlFor="edit-kb-public">Make visible to all clients</label>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn-modal-cancel" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-modal-submit" disabled={loading}>
                            {loading ? "Saving..." : "Save changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
